'use client'

import React from 'react'
import { AdvertisingModalTrigger } from '@/AdvertisingModal/AdvertisingModalTrigger'
import type { AdvertisingModalFormData } from '@/AdvertisingModal/types'
import { useCreateCustomerMutation } from '@/generated/graphql'

// Newsletter popup configuration
const newsletterContent = {
  title: 'Get 16 killer tricks for free!',
  introHtml:
    '<p class="text-xl">Sign up for our newsletter and get 16 killer magic tricks that are easy to do. Start learning magic today.</p>',
  imageUrl: 'https://vinc.gumlet.io/pics/free-magic-signup-andi.jpg',
  imageAlt: 'Newsletter signup',
  submitButtonText: 'Send my free tricks!',
}

const newsletterConfig = {
  layout: 'side-by-side' as const,
  maxWidth: '4xl' as const,
  enableCaptcha: true,
  showOnce: true,
  hideForLoggedInUsers: true,
  showFormForLoggedInUsers: false,
}

export interface NewsletterPopupProps {
  trigger?: 'timer' | 'exit-intent' | 'click'
  triggerDelay?: number
  exitIntentSensitivity?: number
  showOnce?: boolean
  cookieName?: string
  cookieExpireDays?: number
  children?: React.ReactElement
}

export const NewsletterPopup = ({
  trigger = 'timer',
  triggerDelay = 5000,
  exitIntentSensitivity = 30,
  showOnce = false,
  cookieName = 'newsletterPopupShown',
  cookieExpireDays = 30,
  children,
}: NewsletterPopupProps) => {
  const { mutate: createCustomer } = useCreateCustomerMutation({
    onSuccess: result => {
      if (result.createCustomer?.success) {
        // Success handled by the modal's success state
      } else {
        // Error handled by the modal's error state
      }
    },
    onError: () => {
      // Error handled by the modal's error state
    },
  })

  const handleSubmit = async (data: AdvertisingModalFormData) => {
    // Safely parse name into first and last name
    const nameParts = data.name?.split(' ') || []
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    createCustomer({
      firstName,
      lastName,
      email: data.email,
      howSignedUp: 1, // Web signup
      addToNewsletter: true,
      skipEmailValidation: false,
    })
  }

  return (
    <AdvertisingModalTrigger
      trigger={trigger}
      triggerDelay={triggerDelay}
      exitIntentSensitivity={exitIntentSensitivity}
      showOnce={showOnce}
      cookieName={cookieName}
      cookieExpireDays={cookieExpireDays}
      content={newsletterContent}
      config={newsletterConfig}
      onSubmit={handleSubmit}
    >
      {children}
    </AdvertisingModalTrigger>
  )
}
